import {
  HTMLAttributes,
  ReactNode,
  RefAttributes,
  createContext,
  forwardRef,
  useContext,
} from 'react';
import classnames from 'classnames';
import {
  Menu as BaseMenu,
  MenuItem as BaseMenuItem,
  MenuButton,
  MenuButtonArrow,
  useMenuState,
} from 'ariakit/menu';
import { As } from 'ariakit-utils/types';

// Use React Context so we can determine if the menu is a submenu or not.
const MenuContext = createContext(false);

export type MenuItemProps = HTMLAttributes<HTMLDivElement> & {
  label: ReactNode;
  disabled?: boolean;
  as?: As;
};

export const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  ({ label, as, ...props }, ref) => {
    return (
      <BaseMenuItem className="menu-item" ref={ref} as={as || 'div'} {...props}>
        {label}
      </BaseMenuItem>
    );
  },
);

export type MenuProps = HTMLAttributes<HTMLDivElement> & {
  label: ReactNode;
  disabled?: boolean;
};

type MenuButtonProps = HTMLAttributes<HTMLDivElement> &
  RefAttributes<HTMLDivElement>;

export const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ({ label, children, ...props }, ref) => {
    const inSubmenu = useContext(MenuContext);
    const menu = useMenuState({
      gutter: inSubmenu ? 8 : 4,
      shift: inSubmenu ? -9 : 0,
    });

    const renderMenuButton = (menuButtonProps: MenuButtonProps) => (
      <MenuButton
        state={menu}
        {...menuButtonProps}
        className={classnames(menuButtonProps.className, 'menu-trigger')}
      >
        <span className="label">{label}</span>
        <MenuButtonArrow />
      </MenuButton>
    );

    return (
      <>
        {inSubmenu ? (
          // If it's a submenu, we have to combine the MenuButton and the
          // MenuItem components into a single component, so it works as a
          // submenu button.
          <BaseMenuItem className="menu-item" ref={ref} {...props}>
            {renderMenuButton}
          </BaseMenuItem>
        ) : (
          // Otherwise, we just render the menu button.
          renderMenuButton({ ref, ...props })
        )}
        <BaseMenu state={menu} className="menu">
          <MenuContext.Provider value={true}>{children}</MenuContext.Provider>
        </BaseMenu>
      </>
    );
  },
);
